import { Box, Button, FormControl, Grid, InputLabel, List, ListItem, ListItemButton, ListItemText, ListSubheader, MenuItem, Popover, Select, Stack, Tab, Tabs, Typography } from "@mui/material";
import { DataGrid } from '@mui/x-data-grid';
import { DownDetectorTable } from "./DownDetectorTable";
import { DownDetectorItem } from "./DownDetectorItem";
import { DownDetectorChart } from "./DownDetectorChart";
import { useState } from "react";
import { TabPanel } from "../../components/TabPanel";
import { DOWN_DETECTOR_CHART_TYPE, DOWN_DETECTOR_TIME_INTERVAL, DOWN_DETECTOR_TIME_RANGE } from "../../utils/constants";
import moment from "moment";
import { useFetchData } from "../../hooks/useFetchData";
import { useParams } from "react-router-dom";
import { DownDetectorTitle } from "./DownDetectorTitle";
import { DownDetectorPopover } from "./DownDetectorPopover";
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { DateTimePicker } from "@mui/x-date-pickers";
import { downloadJSONAsCSV } from "../../utils/export";
import { DownDetectorExtendedChart } from "./DownDetectorExtendedChart";

const bankingCompanies = [
    {
        name: "US Bank (US)",
        color: "#2E96FF",
        disabled: false
    },
    {
        name: "Chase (US)",
        color: "#00AA55",
        disabled: false
    },
    {
        name: "Wells Fargo (US)",
        color: "#0C2074",
        disabled: false
    },
    {
        name: "Bank of America (US)",
        color: "#6518A5",
        disabled: false
    },
    {
        name: "Citi (US)",
        color: "#F56601",
        disabled: false
    },
    {
        name: "PNC (US)",
        color: "#00A5A8",
        disabled: false
    },
];

const ispCompanies = [
    {
        name: "AT&T (US)",
        color: "#2E96FF",
        disabled: false
    },
    {
        name: "CenturyLink (US)",
        color: "#00AA55",
        disabled: false
    },
    {
        name: "T-Mobile (US)",
        color: "#0C2074",
        disabled: false
    },
    {
        name: "Verizon (US)",
        color: "#6518A5",
        disabled: false
    },
    {
        name: "Xfinity by Comcast (US)",
        color: "#F56601",
        disabled: false
    },
];

const summaryColumns = [
    { field: 'company', headerName: 'Company', flex: 1},
    { field: 'count', headerName: 'Count', flex: 1, type: 'number'}
];

const reduceCompanyData = (data, company) => {
    return {
        company: company,
        count: data.reduce((total, value) => total + (parseInt(value[company]) || 0), 0)
    }
};

const createCompanyRowData = (data, companies) => {
    return companies.map((company, index) => {
        return {
            id: index,
            ...reduceCompanyData(data, company)
        };
    });
};

export const getIntervalData = (data, timeInterval = "any") => {
    return data?.[timeInterval] || [];
}

export const DownDetector = () => {
    const currentTime = new Date();

    const toToday = moment(currentTime).endOf("day");
    const fromToday = moment(currentTime).startOf("day");

    const toYesterday = moment(currentTime).subtract(1, "day").endOf("day");
    const fromYesterday = moment(currentTime).subtract(1, "day").startOf("day");

    const toWeek = moment(currentTime);
    const fromWeek = moment(currentTime).subtract(7, "days");

    const toMonth = moment(currentTime);
    const fromMonth = moment(currentTime).subtract(30, "days");

    const [closeTimeRangePopoverToggle, setCloseTimeRangePopoverToggle] = useState(false);

    const [timeRange, setTimeRange] = useState(DOWN_DETECTOR_TIME_RANGE.TODAY);

    const [appliedTimeRange, setAppliedTimeRange] = useState(DOWN_DETECTOR_TIME_RANGE.TODAY);

    const [toTime, setToTime] = useState(moment(toToday));
    const [fromTime, setFromTime] = useState(moment(fromToday));

    const [toTimeApplied, setToTimeApplied] = useState(toTime);
    const [fromTimeApplied, setFromTimeApplied] = useState(fromTime);

    const [timeRangeParams, setTimeRangeParams] = useState({
        from: fromTime.format("YYYY-MM-DD HH:mm:ss"),
        to: toTime.format("YYYY-MM-DD HH:mm:ss"),
    });

    const { status: bankingDataStatus, data: bankingData } = useFetchData({
        endpoint: `downdetector/benchmarking/banking`,
        params: timeRangeParams,
        defaultData: []
    });

    const { status: ispDataStatus, data: ispData } = useFetchData({
        endpoint: `downdetector/benchmarking/isp`,
        params: timeRangeParams,
        defaultData: []
    });

    const toggleCloseTimeRangePopover = () => {
        setCloseTimeRangePopoverToggle(!closeTimeRangePopoverToggle);
    };

    const onTimeRangeChange = (e) => {
        setTimeRange(e?.target?.value);

        if(e?.target?.value === DOWN_DETECTOR_TIME_RANGE.TODAY) {
            setToTime(toToday);
            setFromTime(fromToday);
        } else if(e?.target?.value === DOWN_DETECTOR_TIME_RANGE.YESTERDAY) {
            setToTime(toYesterday);
            setFromTime(fromYesterday);
        } else if(e?.target?.value === DOWN_DETECTOR_TIME_RANGE.DAYS_7) {
            setToTime(toWeek);
            setFromTime(fromWeek);
        } else if(e?.target?.value === DOWN_DETECTOR_TIME_RANGE.DAYS_30) {
            setToTime(toMonth);
            setFromTime(fromMonth);
        }
    }

    const onTimeRangeApply = (e) => {
        setTimeRangeParams({
            from: fromTime.format("YYYY-MM-DD HH:mm:ss"),
            to: toTime.format("YYYY-MM-DD HH:mm:ss"),
        });

        setToTimeApplied(toTime);
        setFromTimeApplied(fromTime);
        setAppliedTimeRange(timeRange);
        toggleCloseTimeRangePopover();
    };

    const onTimeRangeCancel = (e) => {
        setToTime(toTimeApplied);
        setFromTime(fromTimeApplied);
        setTimeRange(appliedTimeRange);
        toggleCloseTimeRangePopover();
    };

    return (
        <Grid container justifyContent="center" sx={{ px: 10, mt: 0 }} spacing={1}>
            <Grid item xs={12} md={12}>
                <DownDetectorItem sx={{display: 'flex', justifyContent: 'end', alignItems: 'center'}}>
                    <Box
                        sx={{ mr: "auto", ml: 2 }}
                    >
                        <Typography
                            variant="h5"
                            fontWeight="bold"
                            align="left"
                            alignSelf="center"
                            component="div"
                        >
                            Social Media Benchmarking
                        </Typography>

                        <Typography
                            variant="body1"
                            align="left"
                            alignSelf="center"
                            component="div"
                        >
                            {`Time Range: ${fromTimeApplied.format("MM/DD/YYYY HH:mm:ss")} - ${toTimeApplied.format("MM/DD/YYYY HH:mm:ss")}`}
                        </Typography>
                    </Box>

                    <DownDetectorPopover closePopoverToggle={closeTimeRangePopoverToggle} buttonContent={<><AccessTimeIcon sx={{mr: 1}} /><Typography>{timeRange.replace(/_/g, " ")}</Typography></>}>
                        <Grid container justifyContent="center" sx={{ py: 3, px: 2, width: "525px" }} spacing={2}>
                            <Grid item xs={12} md={12}>
                                <FormControl variant="outlined" sx={{ ml: "auto", width: "100%" }}>
                                    <InputLabel id="select-filter-type">Time Range</InputLabel>

                                    <Select
                                        variant="outlined"
                                        labelId="select-filter-type"
                                        value={timeRange}
                                        label="Filter Type"
                                        autoWidth
                                        onChange={onTimeRangeChange}>
                                        <MenuItem default value={DOWN_DETECTOR_TIME_RANGE.TODAY}>Today</MenuItem>
                                        <MenuItem value={DOWN_DETECTOR_TIME_RANGE.YESTERDAY}>Yesterday</MenuItem>
                                        <MenuItem value={DOWN_DETECTOR_TIME_RANGE.DAYS_7}>Last 7 Days</MenuItem>
                                        <MenuItem value={DOWN_DETECTOR_TIME_RANGE.DAYS_30}>Last 30 Days</MenuItem>
                                        <MenuItem value={DOWN_DETECTOR_TIME_RANGE.CUSTOM}>Custom</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={6} md={6}>
                                <DateTimePicker label="From" disabled={timeRange !== DOWN_DETECTOR_TIME_RANGE.CUSTOM} value={fromTime} onChange={(newTime) => setFromTime(newTime)} fullWidth/>
                            </Grid>

                            <Grid item xs={6} md={6}>
                                <DateTimePicker label="To" disabled={timeRange !== DOWN_DETECTOR_TIME_RANGE.CUSTOM} value={toTime} onChange={(newTime) => setToTime(newTime)} fullWidth/>
                            </Grid>

                            <Grid item xs={12} md={12} sx={{display: 'flex', justifyContent: 'end', alignItems: 'center', mt: 2}}>
                                <Button variant="outlined" disabled={bankingDataStatus === "loading"} onClick={onTimeRangeCancel}>Cancel</Button>
                                <Button variant="contained" disabled={bankingDataStatus === "loading"} sx={{ml: 1}} onClick={onTimeRangeApply}>Apply</Button>
                            </Grid>
                        </Grid>
                    </DownDetectorPopover>
                </DownDetectorItem>
            </Grid>

            <Grid item xs={12} md={12}>
                <DownDetectorExtendedChart title="Banking Reports Over Time" data={bankingData} companies={bankingCompanies}/>
            </Grid>

            <Grid item xs={12} md={12}>
                <DownDetectorExtendedChart title="ISP Reports Over Time" data={ispData} companies={ispCompanies}/>
            </Grid>

            <Grid item xs={6} md={6} >
                <DownDetectorItem title="Total Banking Reports">
                    <DownDetectorTable loading={bankingDataStatus === "loading"} columns={summaryColumns} rows={createCompanyRowData(getIntervalData(bankingData), [
                        "Wells Fargo (US)",
                        "Chase (US)",
                        "PNC (US)",
                        "Bank of America (US)",
                        "US Bank (US)",
                    ])}/>
                </DownDetectorItem>
            </Grid>

            <Grid item xs={6} md={6} >
                <DownDetectorItem title="Total ISP Reports">
                    <DownDetectorTable loading={bankingDataStatus === "loading"} columns={summaryColumns} rows={createCompanyRowData(getIntervalData(ispData), [
                        "AT&T (US)",
                        "CenturyLink (US)",
                        "T-Mobile (US)",
                        "Verizon (US)",
                        "Xfinity by Comcast (US)"
                    ])}/>
                </DownDetectorItem>
            </Grid>
        </Grid>
    );
};